// Pause-modal preview of the pilot's currently equipped moonstone skin.
// Renders a small (40px) animated crescent using the same `drawMoonstone`
// engine the in-game stones use, so the pause panel and the field stones
// feel visually identical. When `ownedSkins` + `skinsCatalog` + `onSwap`
// are provided, also renders a horizontal switcher row letting the pilot
// hot-swap between owned skins without leaving the run.
import { useEffect, useRef } from "react";
import { Lock } from "lucide-react";
import { drawMoonstone } from "../engine/draw";

// Small animated crescent — used for the big preview AND the per-option
// swap chips. `interactive` toggles a hover ring; `dimmed` fades it for
// locked skins.
function CrescentChip({ palette, size, dimmed = false }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    let raf = 0;
    let alive = true;
    const loop = () => {
      if (!alive) return;
      ctx.clearRect(0, 0, size, size);
      drawMoonstone(
        ctx,
        { x: size / 2, y: size / 2, r: size * 0.36, seed: 0.8 },
        palette,
      );
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [palette, size]);
  return (
    <canvas
      ref={canvasRef}
      style={{ opacity: dimmed ? 0.35 : 1, filter: dimmed ? "grayscale(0.6)" : "none" }}
    />
  );
}

export function SkinPreview({
  palette,
  name,
  size = 40,
  ownedSkins,
  skinsCatalog,
  activeSkin,
  onSwap,
  onTry,
}) {
  const tint = palette?.mid || "#c084fc";
  // Switcher only renders when we have a catalog AND a swap callback. The
  // catalog drives the option order so the row stays consistent with the
  // /skins page (Opal · Gold · Sapphire · Cosmic Rainbow).
  const switcher = Array.isArray(skinsCatalog) && skinsCatalog.length > 0 && typeof onSwap === "function";
  return (
    <div
      className="flex flex-col items-stretch gap-2 px-3 py-2 border bg-black/40"
      style={{ borderColor: `${tint}66` }}
      data-testid="pause-skin-preview"
    >
      <div className="flex items-center gap-2">
        <CrescentChip palette={palette} size={size} />
        <div className="flex flex-col leading-tight">
          <span className="font-mono-cosmic text-[8px] tracking-[0.3em] uppercase text-white/45">
            Equipped
          </span>
          <span
            className="font-heading uppercase text-[11px] tracking-tight"
            style={{ color: tint }}
            data-testid="pause-skin-preview-name"
          >
            {name || "Opal"}
          </span>
        </div>
      </div>
      {switcher && (
        <div
          className="flex items-center justify-center gap-2 pt-1.5 border-t border-white/10"
          data-testid="pause-skin-switcher"
        >
          {skinsCatalog.map((s) => {
            const owned = (ownedSkins || []).includes(s.id);
            const active = s.id === activeSkin;
            const ring = active ? `${s.palette?.mid || tint}` : owned ? "#ffffff22" : "#ffffff10";
            // Locked chips are clickable when an `onTry` callback is
            // supplied — they trigger a temporary visual preview of the
            // field stones in that palette so the pilot can see what
            // they're buying. Owned chips swap; the active chip is inert.
            const handleClick = () => {
              if (active) return;
              if (owned) onSwap(s.id);
              else if (typeof onTry === "function") onTry(s.id);
            };
            const clickable = !active && (owned || typeof onTry === "function");
            return (
              <button
                key={s.id}
                type="button"
                onClick={handleClick}
                disabled={!clickable}
                title={
                  owned
                    ? `${s.name}${active ? " · Equipped" : " · Click to equip"}`
                    : `${s.name} · Locked${typeof onTry === "function" ? " · Click to preview · " + (s.cost || 0) + " ☾ on /skins" : " · buy on /skins"}`
                }
                className={`relative w-9 h-9 flex items-center justify-center transition ${
                  clickable
                    ? "cursor-pointer hover:scale-110"
                    : "cursor-default"
                }`}
                style={{
                  border: `1px solid ${ring}`,
                  boxShadow: active ? `0 0 8px ${ring}aa, inset 0 0 6px ${ring}55` : undefined,
                  background: active ? `${ring}11` : "transparent",
                }}
                data-testid={`pause-skin-option-${s.id}`}
              >
                <CrescentChip palette={s.palette} size={26} dimmed={!owned} />
                {!owned && (
                  <Lock className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-white/45" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
